import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import { money, toMoney } from './money.js'

/**
 * Selling prices and unit packs (ADR-045). One implementation for the web's previews and the API.
 *
 * A product is stocked in its base unit (piece, kg). It may also be sold and bought in packs of its own
 * (Carton = 12 pieces): every pack is a unit of the units list with a factor to the base unit. Stock,
 * cost and serials always move in base units; a document line keeps the unit it was entered in and that
 * unit's factor.
 *
 * Each variant has a price per level (Retail, Wholesale, Distributor) per unit. The Retail price of the
 * base unit is the variant's list price and is always set; every other cell is optional. A customer's
 * custom price (السعر المخصص), or one of their group's, beats the levels. A missing cell falls back:
 *   custom price:  customer's own (unit, else base × factor), else their groups' lowest (likewise);
 *   level price:   the level's (unit, else base × factor), else Retail less the level's default discount;
 *   Retail:        (unit, else base × factor).
 * Prices exclude VAT; a tax-inclusive document adds it (`sellingPrice`).
 */

export const PRICE_LEVELS = ['RETAIL', 'WHOLESALE', 'DISTRIBUTOR'] as const
export const priceLevelSchema = z.enum(PRICE_LEVELS)
export type PriceLevel = z.infer<typeof priceLevelSchema>

/** Where a line's price came from: a level of the grid or a custom price. Null on a line: typed by hand. */
export const PRICE_SOURCES = ['RETAIL', 'WHOLESALE', 'DISTRIBUTOR', 'CUSTOM'] as const
export const priceSourceSchema = z.enum(PRICE_SOURCES)
export type PriceSource = z.infer<typeof priceSourceSchema>

const LEVEL_RANK: Record<PriceLevel, number> = { RETAIL: 0, WHOLESALE: 1, DISTRIBUTOR: 2 }

/**
 * The level a customer buys at: their own, else the deepest level among their groups (Distributor before
 * Wholesale before Retail), else Retail.
 */
export function effectivePriceLevel(
  own: PriceLevel | null | undefined,
  groupLevels: readonly (PriceLevel | null | undefined)[] = [],
): PriceLevel {
  if (own) return own
  let best: PriceLevel = 'RETAIL'
  for (const level of groupLevels) if (level && LEVEL_RANK[level] > LEVEL_RANK[best]) best = level
  return best
}

/** Percent off Retail a level is sold at when it has no price of its own (setting group `pricing`). */
export interface LevelDiscounts {
  WHOLESALE: string | number
  DISTRIBUTOR: string | number
}
export const NO_LEVEL_DISCOUNTS: LevelDiscounts = { WHOLESALE: 0, DISTRIBUTOR: 0 }

/** A unit a variant is sold or bought in: its base unit (factor 1) or one of its product's packs. */
export interface SaleUnit {
  unitId: string
  name: string
  nameAr: string | null
  symbol: string
  symbolAr: string | null
  /** Base units in one of this unit ("12" for a carton of 12); "1" for the base unit. */
  factor: string
  /** Whether quantities in this unit may have decimals. */
  allowFractions: boolean
  /** The pack's own barcode on this variant; null for the base unit (the variant's barcode). */
  barcode: string | null
  isBase: boolean
}

/** An explicit price of the grid: a level in a unit. The base Retail cell is the variant's list price. */
export interface PriceCell {
  unitId: string
  level: PriceLevel
  price: string
}

/**
 * A custom price that applies to the customer on the document's date, in a unit: their own
 * (`CUSTOMER`), or the lowest of their groups' (`GROUP`).
 */
export interface CustomPriceCell {
  unitId: string
  price: string
  scope: 'CUSTOMER' | 'GROUP'
}

/** Everything that prices one variant: its units (base first), its grid, and the customer's custom prices. */
export interface VariantPricing {
  units: SaleUnit[]
  prices: PriceCell[]
  /** Present when priced for a customer. */
  customPrices?: CustomPriceCell[]
}

export interface ResolvedPrice {
  /** Ex-VAT, 3 dp. */
  price: string
  source: PriceSource
}

/**
 * The ex-VAT price of one unit of the variant for a customer buying at `level`, and where it came from.
 * Null when the unit is not one of the variant's units.
 */
export function resolvePrice(
  pricing: VariantPricing,
  unitId: string,
  level: PriceLevel,
  discounts: LevelDiscounts = NO_LEVEL_DISCOUNTS,
): ResolvedPrice | null {
  const unit = pricing.units.find((u) => u.unitId === unitId)
  const base = pricing.units.find((u) => u.isBase)
  if (!unit || !base) return null
  const factor = money(unit.factor)
  const scaled = (price: string | undefined) =>
    price === undefined ? undefined : toMoney(money(price).times(factor))

  for (const scope of ['CUSTOMER', 'GROUP'] as const) {
    const cells = (pricing.customPrices ?? []).filter((c) => c.scope === scope)
    const own = cells.find((c) => c.unitId === unitId)?.price
    const custom =
      own !== undefined ? toMoney(own) : scaled(cells.find((c) => c.unitId === base.unitId)?.price)
    if (custom !== undefined) return { price: custom, source: 'CUSTOM' }
  }

  const levelPrice = (l: PriceLevel) => {
    const own = pricing.prices.find((c) => c.level === l && c.unitId === unitId)?.price
    if (own !== undefined) return toMoney(own)
    return scaled(pricing.prices.find((c) => c.level === l && c.unitId === base.unitId)?.price)
  }
  const retail = levelPrice('RETAIL') ?? '0.000'
  if (level !== 'RETAIL') {
    const own = levelPrice(level)
    if (own !== undefined) return { price: own, source: level }
    const pct = money(discounts[level] || 0)
    if (pct.greaterThan(0)) {
      return { price: toMoney(money(retail).times(money(100).minus(pct)).dividedBy(100)), source: level }
    }
  }
  return { price: retail, source: 'RETAIL' }
}

/** The unit a line starts in: the scanned pack, else the base unit. */
export function baseUnitOf(pricing: Pick<VariantPricing, 'units'>): SaleUnit | undefined {
  return pricing.units.find((u) => u.isBase)
}

/** Quantity in base units: quantity × factor, exact. */
export function baseQuantity(quantity: string, factor: string | null | undefined): string {
  return money(quantity)
    .times(factor ?? 1)
    .toFixed()
}

/**
 * Why a quantity cannot be entered in a unit, or null: a whole-number unit given a fraction, or a pack
 * quantity that makes a fraction of a whole-number base unit (1.1 cartons of 12 = 13.2 pieces), or more
 * than the 4 decimals stock keeps.
 */
export function unitQuantityError(
  quantity: string,
  unit: Pick<SaleUnit, 'allowFractions' | 'factor'>,
  base: Pick<SaleUnit, 'allowFractions'>,
): 'WHOLE_UNITS' | 'WHOLE_BASE_UNITS' | 'BASE_SCALE' | null {
  const qty = money(quantity)
  if (!unit.allowFractions && !qty.isInteger()) return 'WHOLE_UNITS'
  const inBase = qty.times(unit.factor)
  if (!base.allowFractions && !inBase.isInteger()) return 'WHOLE_BASE_UNITS'
  if (inBase.decimalPlaces() > 4) return 'BASE_SCALE'
  return null
}

/** Messages of `unitQuantityError`, as the API words them. */
export const UNIT_QUANTITY_MESSAGES = {
  WHOLE_UNITS: 'This unit is counted in whole units',
  WHOLE_BASE_UNITS: 'This quantity makes part of a whole base unit',
  BASE_SCALE: 'This quantity makes more than 4 decimals of the base unit',
} as const

/** A pack of a product: a unit of the units list holding `factor` base units. */
const PACK_FACTOR = /^\d{1,10}(\.\d{1,4})?$/
export const productPackInputSchema = z.object({
  unitId: z.uuid(),
  factor: z
    .string()
    .regex(PACK_FACTOR, 'Enter how many base units the pack holds')
    // zod runs every check: a malformed value must not reach money() (it would throw, not refuse).
    .refine((v) => !PACK_FACTOR.test(v) || money(v).greaterThan(1), 'A pack holds more than one base unit'),
})
export type ProductPackInput = z.input<typeof productPackInputSchema>

/**
 * One explicit cell of a variant's grid other than the base Retail price (that is the variant's
 * `price`). `unitId` null: the base unit.
 */
export const levelPriceInputSchema = z.object({
  unitId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  level: priceLevelSchema,
  price: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
})
export type LevelPriceInput = z.input<typeof levelPriceInputSchema>

export const packBarcodeInputSchema = z.object({
  unitId: z.uuid(),
  barcode: z.string().trim().min(1).max(50),
})

// ── custom prices ──────────────────────────────────────────────────────────────

const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)

const customPriceFields = z.object({
  /** Exactly one of customer and group. */
  customerId: nullableUuid,
  groupId: nullableUuid,
  variantId: z.uuid(),
  /** Null: the base unit (a pack then costs this × its factor unless it has its own row). */
  unitId: nullableUuid,
  price: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
  validFrom: nullableDate,
  validTo: nullableDate,
  notes: optionalText(300),
})
const oneScope = {
  path: ['customerId'],
  message: 'Choose a customer or a customer group',
}
const scopeOk = (v: { customerId: string | null; groupId: string | null }) =>
  (v.customerId === null) !== (v.groupId === null)
const datesOk = (v: { validFrom: string | null; validTo: string | null }) =>
  v.validFrom === null || v.validTo === null || v.validTo >= v.validFrom
const datesInOrder = { path: ['validTo'], message: 'The end date cannot be before the start date' }

export const customPriceInputSchema = customPriceFields
  .refine(scopeOk, oneScope)
  .refine(datesOk, datesInOrder)
export type CustomPriceInput = z.input<typeof customPriceInputSchema>
export const customPriceUpdateSchema = customPriceFields
  .extend({ version: versionSchema })
  .refine(scopeOk, oneScope)
  .refine(datesOk, datesInOrder)
export type CustomPriceUpdate = z.input<typeof customPriceUpdateSchema>

/** `GET /custom-prices`: newest first by default. `current`: valid today or later (not expired). */
export const customPriceListQuerySchema = pageQuerySchema.extend({
  productId: z.uuid().optional(),
  variantId: z.uuid().optional(),
  customerId: z.uuid().optional(),
  groupId: z.uuid().optional(),
  current: z.stringbool().optional(),
})
export type CustomPriceListQuery = z.input<typeof customPriceListQuerySchema>

export interface CustomPrice {
  id: string
  customer: { id: string; code: string; name: string; nameAr: string | null } | null
  group: { id: string; name: string; nameAr: string | null } | null
  variant: { id: string; productId: string; label: string; labelAr: string | null }
  unit: { id: string; symbol: string; symbolAr: string | null; factor: string; isBase: boolean }
  price: string
  validFrom: string | null
  validTo: string | null
  notes: string | null
  version: number
  updatedAt: string
}

// ── pricing a document ─────────────────────────────────────────────────────────

/**
 * `GET /product-variants/prices`: the pricing of known variants for a customer on a date (a document
 * whose customer changed re-prices its lines). Without `customerId`: the Retail level, no custom prices.
 */
export const variantPricesQuerySchema = z.object({
  variantIds: z
    .string()
    .transform((v) => [...new Set(v.split(',').filter(Boolean))])
    .pipe(z.array(z.uuid()).min(1).max(200)),
  customerId: z.uuid().optional(),
  date: z.iso.date().optional(),
})
export type VariantPricesQuery = z.input<typeof variantPricesQuerySchema>

export interface VariantPrices extends VariantPricing {
  variantId: string
}

export interface VariantPricesResult {
  /** The customer's level (Retail without a customer). */
  level: PriceLevel
  items: VariantPrices[]
}
